import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';

interface Competition {
  id: string;
  name: string;
  city: string;
  venue: string;
  start_date: string;
  end_date: string;
  event_ids: string[];
  competitor_limit: number | null;
  url: string;
  registration_open: string;
  registration_close: string;
}

const EVENT_ICONS: Record<string, string> = {
  '333': '3x3',
  '222': '2x2',
  '444': '4x4',
  '555': '5x5',
  '666': '6x6',
  '777': '7x7',
  '333bf': '3BLD',
  '333oh': '3OH',
  '333fm': 'FMC',
  'clock': 'Clock',
  'minx': 'Mega',
  'pyram': 'Pyra',
  'skewb': 'Skewb',
  'sq1': 'SQ1',
  '444bf': '4BLD',
  '555bf': '5BLD',
  '333mbf': 'MBLD',
};

export default function Competitions() {
  const { t } = useTranslation();
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCompetitions = async () => {
      try {
        const today = new Date().toISOString().split('T')[0];
        const response = await fetch(
          `https://www.worldcubeassociation.org/api/v0/competitions?country_iso2=GR&start=${today}`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch competitions');
        }

        const data = await response.json();
        setCompetitions(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchCompetitions();
  }, []);

  const formatDate = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    const pad = (n: number) => n.toString().padStart(2, '0');

    const formatSingle = (date: Date) => {
      return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
    };

    if (startDate === endDate) {
      return formatSingle(start);
    }

    // Same month and year
    if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
      return `${pad(start.getDate())}-${pad(end.getDate())}/${pad(end.getMonth() + 1)}/${end.getFullYear()}`;
    }

    // Different months
    return `${pad(start.getDate())}/${pad(start.getMonth() + 1)} - ${formatSingle(end)}`;
  };

  const isRegistrationOpen = (regOpen: string, regClose: string) => {
    const now = new Date();
    const open = new Date(regOpen);
    const close = new Date(regClose);
    return now >= open && now <= close;
  };

  return (
    <div className="bg-gray-50 min-h-[70vh]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
          {t('competitions.title')}
        </h1>
        <p className="text-gray-600 mb-8">
          {t('competitions.subtitle')}
        </p>

        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {t('competitions.error')}: {error}
          </div>
        )}

        {!loading && !error && competitions.length === 0 && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg">
            {t('competitions.noCompetitions')}
          </div>
        )}

        {!loading && !error && competitions.length > 0 && (
          <div className="overflow-x-auto bg-white rounded-xl shadow-md">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('competitions.table.name')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('competitions.table.date')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('competitions.table.location')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('competitions.table.events')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('competitions.table.status')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {competitions.map((comp) => (
                  <tr key={comp.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <a
                        href={comp.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 font-medium hover:underline"
                      >
                        {comp.name}
                      </a>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatDate(comp.start_date, comp.end_date)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      <div>{comp.city}</div>
                      <div className="text-gray-400 text-xs">{comp.venue}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {comp.event_ids.slice(0, 6).map((event) => (
                          <span
                            key={event}
                            className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded"
                            title={event}
                          >
                            {EVENT_ICONS[event] || event}
                          </span>
                        ))}
                        {comp.event_ids.length > 6 && (
                          <span className="inline-block bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded">
                            +{comp.event_ids.length - 6}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {isRegistrationOpen(comp.registration_open, comp.registration_close) ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {t('competitions.table.registrationOpen')}
                        </span>
                      ) : new Date(comp.registration_open) > new Date() ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          {t('competitions.table.upcoming')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {t('competitions.table.registrationClosed')}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-8 text-center text-sm text-gray-500">
          {t('competitions.poweredBy')}{' '}
          <a
            href="https://www.worldcubeassociation.org"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            World Cube Association
          </a>
        </div>
      </div>
    </div>
  );
}
